import { AfterViewInit, ChangeDetectorRef, Component, Inject, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Service, ServiceFunction } from 'app/modules/user/services/services.types';
import { User } from 'app/core/user/user.types';
import { UserService } from 'app/core/user/user.service';
import { ServicesService } from 'app/modules/user/services/services.service';
import { take, takeUntil } from 'rxjs/operators';
import { PlatformSettings } from 'app/core/platform/platform.types';
import { PlatformService } from 'app/core/platform/platform.service';
import { ReleasesService } from 'app/modules/user/releases/releases.service';
import { PaymentType } from 'app/shared/shared.types';
import { Label, Release } from 'app/modules/user/releases/releases.types';
import { PaymentsService } from 'app/modules/user/payments/payments.service';
import { PaymentMethod, PaymentStatus } from 'app/modules/user/payments/payments.types';
import { IPayPalConfig } from 'ngx-paypal';
import { Subject } from 'rxjs';
import { FuseConfigService } from '@fuse/services/config';
import { AppConfig } from 'app/core/config/app.config';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

@Component({
    selector: 'app-services-modal',
    templateUrl: './services-modal.component.html',
    styleUrls: ['./services-modal.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class ServicesModalComponent implements OnInit, AfterViewInit, OnDestroy {

    releases: Release[];
    labels: Label[];
    editMode: boolean;
    service: Service;
    selectedReleaseId: string;
    selectedLabelId: string;
    serviceForm: FormGroup;
    serviceManagement: boolean;
    platform: PlatformSettings
    user: User;
    config: AppConfig;
    availableServiceFunctions: ServiceFunction[];

    paymentStatus: PaymentStatus;
    public payPalConfig?: IPayPalConfig;
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    images = [
        { url: 'assets/images/apps/services/youtube.svg', name: 'YouTube' },
        { url: 'assets/images/apps/services/youtube-2.svg', name: 'YouTube 2' },
        { url: 'assets/images/apps/services/beatport.svg', name: 'Beatport' },
        { url: 'assets/images/apps/services/deezer.svg', name: 'Deezer' },
        { url: 'assets/images/apps/services/email.svg', name: 'Email' },
        { url: 'assets/images/apps/services/generic-promotion.svg', name: 'Generic Promotion' },
        { url: 'assets/images/apps/services/instagram.svg', name: 'Instagram' },
        { url: 'assets/images/apps/services/music.svg', name: 'Music' },
        { url: 'assets/images/apps/services/playlist.svg', name: 'Playlist' },
        { url: 'assets/images/apps/services/radio.svg', name: 'Radio' },
        { url: 'assets/images/apps/services/radio-2.svg', name: 'Radio 2' },
        { url: 'assets/images/apps/services/social-media-promotion.svg', name: 'Social Media' },
        { url: 'assets/images/apps/services/spotify.svg', name: 'Spotify' },
        { url: 'assets/images/apps/services/video.svg', name: 'Video' },
    ]


    constructor(public matDialogRef: MatDialogRef<ServicesModalComponent>,
        private _changeDetectorRef: ChangeDetectorRef,
        private _formBuilder: FormBuilder,
        public userService: UserService,
        private servicesService: ServicesService,
        private _platformService: PlatformService,
        private _paymentsService: PaymentsService,
        private _configService: FuseConfigService,
        public releasesService: ReleasesService,
        @Inject(MAT_DIALOG_DATA) public data: any
    ) {
    }

    ngOnInit(): void {
        this.service = this.data.service;
        if (this.service?.description) {
            this.service.description = this.service.description.replaceAll("<br>", '\n');
        }

        this.serviceManagement = this.data.serviceManagement;
        this.editMode = !!this.data.editMode;

        this._platformService.platform$.pipe(take(1)).subscribe(platform => this.platform = platform);
        this.userService.user$.pipe(take(1)).subscribe(user => this.user = user);

        this._configService.config$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(config => this.config = config);

        if (this.user.role === "user" || this.user.role === "label") {
            this.releasesService.getReleases().pipe(take(1)).subscribe(
                releases => {
                    this.releases = releases.filter(release => release.status === 'published')
                }
            );

            this.releasesService.getLabels().pipe(take(1)).subscribe(
                labels => {
                    this.labels = labels
                }
            );

            const { payPalConfig, payPalStatus$ } = this._paymentsService.initPayPalConfig(this.platform.paypalClientId, this.platform.currency, this.orderService.bind(this));
            this.payPalConfig = payPalConfig;
            payPalStatus$.pipe(takeUntil(this._unsubscribeAll)).subscribe(status => {
                this.paymentStatus = status.status;
                if (this.paymentStatus === "complete") {
                    this.matDialogRef.close({ status: this.paymentStatus, paymentType: "paypal" as PaymentType });
                }
            });
        }

        this.images = this.images.sort((a, b) => a.name < b.name ? -1 : 1)

        this.serviceForm = this._formBuilder.group({
            uuid: [undefined],
            type: ['', Validators.required],
            title: ['', Validators.required],
            image: [''],
            price: [0, [Validators.required, Validators.min(0)]],
            description: [''],
            serviceFunctionUuid: [null],
        });

        if (this.service && this.serviceManagement) {
            this.serviceForm.patchValue(this.service);
        }
    }

    getLabelsWithoutBeatportPermission() {
        return this.labels.filter(label => !label.beatportPermission);
    }

    ngAfterViewInit(): void {
        if (this.editMode) {
            this.servicesService.getUnassignedServiceFunctions(this.service?.uuid).pipe(take(1)).subscribe(
                serviceFunctions => {
                    this.availableServiceFunctions = serviceFunctions;
                    this._changeDetectorRef.markForCheck();
                }
            );
        }
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    getSelectedServiceFunction() {
        return this.availableServiceFunctions?.find(serviceFunction => serviceFunction.uuid === this.serviceForm.get('serviceFunctionUuid').value);
    }

    isPaymentMethodEnabled(method: PaymentMethod) {
        return this.platform.availablePaymentMethods.includes(method);
    }

    private orderService(paymentType: PaymentType) {
        return this._paymentsService.orderService(this.service.uuid, this.selectedReleaseId, this.selectedLabelId, paymentType).pipe(take(1));
    }

    orderWithStripe() {
        this.orderService("stripe").subscribe({
            next: (result) => {
                this._paymentsService.redirectToStripeCheckout(result.clientKey, result.orderId).pipe(take(1)).subscribe(error => {
                    console.error(error.error);
                });
            },
            error: (error) => {
                this.paymentStatus = "failed";
            }
        })
    }

    orderWithBalance() {
        this.orderService("balance").subscribe({
            next: (result) => {
                this.paymentStatus = result.status;
                this.matDialogRef.close({ status: result.status, paymentType: result.paymentType });
            },
            error: (error) => {
                this.paymentStatus = "failed";
            }
        })
    }

    orderFreeService() {
        this._paymentsService.orderService(this.service.uuid, this.selectedReleaseId, this.selectedLabelId).pipe(take(1)).subscribe(_ => {
            this.matDialogRef.close({ status: "success" });
        })
    }

    getCurrentImageName(): string {
        return this.images.find(image => image.url === this.serviceForm.get('image').value)?.name ?? 'Unknown';
    }

    toggleEditMode(editMode: boolean | null = null): void {
        if (editMode === null) {
            this.editMode = !this.editMode;
        } else {
            this.editMode = editMode;
        }
        this._changeDetectorRef.markForCheck();
    }

    onSubmit() {
        if (!this.serviceForm.valid) {
            return;
        }

        this.servicesService.saveService(this.serviceForm.getRawValue()).pipe(take(1)).subscribe(() => this.matDialogRef.close());
    }

    onCancel() {
        this.serviceForm.reset();
        this.matDialogRef.close();
    }

    closeModal() {
        this.matDialogRef.close();
    }
}
